import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { CompositeCommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { UpdateTableCellStylesCommand } from '../../core/commands/UpdateTableCellStylesCommand';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { generateDarkModeColorPalette } from '../../react-app/theme/color-utils';
import { Check, Plus, Pipette } from 'lucide-react';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { useTranslation } from '../../hooks/useTranslation';
import { useTableCellStore, type TableCellKey } from '../../stores/tableCellStore';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const HEX_FULL_REGEX = /^#[0-9A-F]{6}$/;
const HEX_SHORT_REGEX = /^#[0-9A-F]{3}$/;

const expandHex = (hex: string) => {
  const normalized = hex.trim().toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(normalized)) return normalized;
  if (/^#[0-9A-F]{3}$/.test(normalized)) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return '#3662E3';
};

const hexToRgb = (hex: string) => {
  const expanded = expandHex(hex).slice(1);
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number) => {
  const toHex = (component: number) =>
    clamp(Math.round(component), 0, 255).toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsv = (r: number, g: number, b: number) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rn) {
      h = 60 * (((gn - bn) / delta) % 6);
    } else if (max === gn) {
      h = 60 * ((bn - rn) / delta + 2);
    } else {
      h = 60 * ((rn - gn) / delta + 4);
    }
  }
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : delta / max;
  const v = max;
  return { h, s, v };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h >= 0 && h < 60) {
    rp = c;
    gp = x;
  } else if (h >= 60 && h < 120) {
    rp = x;
    gp = c;
  } else if (h >= 120 && h < 180) {
    gp = c;
    bp = x;
  } else if (h >= 180 && h < 240) {
    gp = x;
    bp = c;
  } else if (h >= 240 && h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }

  return {
    r: (rp + m) * 255,
    g: (gp + m) * 255,
    b: (bp + m) * 255
  };
};

const hsvToHex = (h: number, s: number, v: number) => {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
};

interface AdvancedColorPickerProps {
  color: string;
  onColorChange: (hex: string) => void;
}

const AdvancedColorPicker: React.FC<AdvancedColorPickerProps> = ({ color, onColorChange }) => {
  const [{ h, s, v }, setHsv] = React.useState(() => {
    const { r, g, b } = hexToRgb(color);
    return rgbToHsv(r, g, b);
  });

  React.useEffect(() => {
    if (!HEX_FULL_REGEX.test(color) && !HEX_SHORT_REGEX.test(color)) {
      return;
    }
    const { r, g, b } = hexToRgb(color);
    const next = rgbToHsv(r, g, b);
    setHsv(next);
  }, [color]);

  const updateColor = React.useCallback(
    (next: { h?: number; s?: number; v?: number }) => {
      setHsv((prev) => {
        const current = {
          h: next.h !== undefined ? next.h : prev.h,
          s: next.s !== undefined ? next.s : prev.s,
          v: next.v !== undefined ? next.v : prev.v
        };
        const hex = hsvToHex(current.h, current.s, current.v);
        onColorChange(hex);
        return current;
      });
    },
    [onColorChange]
  );

  const handleSaturationValueEvent = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const updateFromPointer = (clientX: number, clientY: number) => {
        const saturation = clamp((clientX - rect.left) / rect.width, 0, 1);
        const value = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
        updateColor({ s: saturation, v: value });
      };
      const handlePointerMove = (moveEvent: PointerEvent) => {
        updateFromPointer(moveEvent.clientX, moveEvent.clientY);
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      updateFromPointer(event.clientX, event.clientY);
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [updateColor]
  );

  const handleHueEvent = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const updateFromPointer = (clientY: number) => {
        const ratio = clamp((clientY - rect.top) / rect.height, 0, 1);
        const hue = ratio * 360;
        updateColor({ h: hue });
      };
      const handlePointerMove = (moveEvent: PointerEvent) => {
        updateFromPointer(moveEvent.clientY);
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      updateFromPointer(event.clientY);
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [updateColor]
  );

  const hueColor = hsvToHex(h, 1, 1);

  return (
    <div className="flex gap-3">
      <div
        className="relative h-[160px] w-[216px] cursor-crosshair rounded-md"
        style={{
          backgroundImage: `
            linear-gradient(0deg, #000000, rgba(0,0,0,0)),
            linear-gradient(90deg, #ffffff, ${hueColor})
          `
        }}
        onPointerDown={handleSaturationValueEvent}
        role="presentation"
        aria-label="Pick saturation and brightness"
      >
        <div
          className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
          style={{
            left: `${s * 100}%`,
            top: `${(1 - v) * 100}%`
          }}
        />
      </div>
      <div
        className="relative h-[160px] w-6 cursor-pointer rounded-md"
        style={{
          background: 'linear-gradient(180deg, #ff0000 0%, #ffff00 16.6%, #00ff00 33.3%, #00ffff 50%, #0000ff 66.6%, #ff00ff 83.3%, #ff0000 100%)'
        }}
        onPointerDown={handleHueEvent}
        role="presentation"
        aria-label="Pick hue"
      >
        <div
          className="pointer-events-none absolute left-1/2 h-2 w-6 -translate-x-1/2 rounded-sm border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
          style={{ top: `${(h / 360) * 100}%` }}
        />
      </div>
    </div>
  );
};

const presetColors = [
  // First row - light warm colors
  ['transparent', '#FCF9C9', '#F3BD7F', '#F6CCCB'],
  // Second row - light cool to warm
  ['#C7F5D3', '#C3E5FA', '#C9D2FA', '#F5D565'],
  // Third row - mid warm to cool
  ['#ED974F', '#E77975', '#77DB89', '#70A3F3'],
  // Fourth row - mid cool to warm  
  ['#A38CF3', '#C18D30', '#70391A', '#CA3A31'],
  // Fifth row - cool dark
  ['#43946C', '#3662E3', '#652CD1', '#FFFFFF'],
  // Sixth row - gray
  ['#E3E8EF', '#97A3B6', '#4A5567', '#000000'],
  
];

const getOrgShapeFillColor = (context: IToolContext): string | null => {
  const selectedIds = Array.from(context.selection);
  if (selectedIds.length === 0) return null;
  for (const id of selectedIds) {
    const shape = context.shapes.get(id) as any;
    const data = shape?.data ?? shape;
    const category = data?.category ?? data?.attrs?.category;
    const type = data?.type as string | undefined;
    if (category !== 'orgchart' && !(type ? type.startsWith('org_') : false)) {
      continue;
    }
    const attrs = (data?.attrs as Record<string, unknown>) ?? {};
    const props = (data?.props as Record<string, unknown>) ?? {};
    const fill = (data?.fillColor as string) || (attrs.fillColor as string) || (props.fillColor as string);
    return fill || '#FFFFFF';
  }
  return null;
};

const resolveSelectedTableCellFill = (context: IToolContext): string | null => {
  const nodeIds = Array.from(context.selection);
  if (nodeIds.length === 0) return null;

  const selections = useTableCellStore.getState().selections;

  for (const nodeId of nodeIds) {
    const sel = selections[nodeId];
    const keys = sel?.selectedKeys ?? [];
    if (keys.length === 0) continue;

    const shape = context.shapes.get(nodeId) as any;
    const node = shape?.data ?? shape;
    const attrs = (node?.attrs ?? node?.data?.attrs ?? {}) as Record<string, unknown>;
    const cellStyles = (attrs.cellStyles as Record<TableCellKey, { fillColor?: string }> | undefined) ?? {};
    const key = (sel?.anchorKey ?? keys[0]) as TableCellKey;
    const color = cellStyles[key]?.fillColor;
    if (typeof color === 'string' && color.length > 0) return color;
  }
  return null;
};

const ShapeColorToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const cellFill = React.useMemo(() => resolveSelectedTableCellFill(context), [context]);
  const orgFill = React.useMemo(() => getOrgShapeFillColor(context), [context]);
  const initialColor = cellFill || orgFill || context.selectionStyles?.fillColor || '#3662E3';
  const [selectedColor, setSelectedColor] = React.useState(initialColor);
  const [customColor, setCustomColor] = React.useState('#3662E3');
  const [customColorDraft, setCustomColorDraft] = React.useState('#3662E3');
  const [showCustomPicker, setShowCustomPicker] = React.useState(false);
  const [isPickingColor, setIsPickingColor] = React.useState(false);
  const [recentCustomColors, setRecentCustomColors] = React.useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.localStorage.getItem('shape-toolbar-recent-custom-colors');
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.slice(0, 4) : [];
    } catch {
      return [];
    }
  });
  const colorInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    const next = cellFill || orgFill || context.selectionStyles?.fillColor || '#3662E3';
    setSelectedColor(next);
    const nextUpper = next.toUpperCase();
    const nextCustomColor = HEX_FULL_REGEX.test(nextUpper) || HEX_SHORT_REGEX.test(nextUpper)
      ? expandHex(nextUpper)
      : '#3662E3';
    setCustomColor(nextCustomColor);
    setCustomColorDraft(nextCustomColor);
  }, [cellFill, context.selectionStyles?.fillColor, orgFill]);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('shape-toolbar-recent-custom-colors', JSON.stringify(recentCustomColors));
    } catch {
      // ignore storage issues silently; palette still works in-session
    }
  }, [recentCustomColors]);
  const { mode } = useThemeMode();

  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;

  const applyCustomColor = React.useCallback(
    (value: string) => {
      if (!HEX_FULL_REGEX.test(value) && !HEX_SHORT_REGEX.test(value)) return;
      const normalized = expandHex(value);
      setCustomColor(normalized);
      setCustomColorDraft(normalized);
      setSelectedColor(normalized);
    },
    [setSelectedColor]
  );
  
  // Generate theme-appropriate color palette
  const colorPalette = React.useMemo(() => {
    if (mode === 'dark') {
      return generateDarkModeColorPalette(presetColors);
    }
    return presetColors;
  }, [mode]);

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    onExecute({ color });
    setShowCustomPicker(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value.toUpperCase();
    applyCustomColor(color);
  };

  const handleCustomColorDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomColorDraft(e.target.value.toUpperCase());
  };

  const trackRecentColor = React.useCallback((color: string) => {
    if (!color) return;
    setRecentCustomColors(prev => {
      const normalized = color.toLowerCase();
      const withoutCurrent = prev.filter(c => c.toLowerCase() !== normalized);
      return [color, ...withoutCurrent].slice(0, 4);
    });
  }, []);

  const handleCustomColorApply = () => {
    if (!HEX_FULL_REGEX.test(customColorDraft) && !HEX_SHORT_REGEX.test(customColorDraft)) {
      return;
    }
    const normalized = expandHex(customColorDraft);
    applyCustomColor(normalized);
    trackRecentColor(normalized);
    handleColorChange(normalized);
  };
  const canUseEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;
  const handleEyeDropperPick = React.useCallback(async () => {
    if (!canUseEyeDropper || isPickingColor) return;
    setIsPickingColor(true);
    try {
      // @ts-expect-error EyeDropper is experimental but supported in modern browsers
      const dropper = new window.EyeDropper();
      const result = await dropper.open();
      if (result?.sRGBHex) {
        applyCustomColor(result.sRGBHex.toUpperCase());
      }
    } catch {
      // swallow cancellation or unsupported errors silently
    } finally {
      setIsPickingColor(false);
    }
  }, [applyCustomColor, canUseEyeDropper, isPickingColor]);
  const flattenedPalette = React.useMemo(() => colorPalette.flat(), [colorPalette]);
  const isColorSelected = React.useCallback(
    (color: string) => selectedColor.toLowerCase() === color.toLowerCase(),
    [selectedColor]
  );
  const renderColorChip = React.useCallback(
    (color: string, key: string, options?: { trackOnSelect?: boolean }) => {
      const selected = isColorSelected(color);
      const isTransparent = color.toLowerCase() === 'transparent';
      const resolveBorderColor = () => {
        if (isTransparent || !color || color === 'transparent') {
          return 'rgba(148, 163, 184, 0.45)';
        }
        try {
          const expanded = color.startsWith('#') && color.length === 4
            ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
            : color;
          if (expanded.startsWith('#')) {
            const r = parseInt(expanded.slice(1, 3), 16);
            const g = parseInt(expanded.slice(3, 5), 16);
            const b = parseInt(expanded.slice(5, 7), 16);
            const darken = (component: number) => Math.max(component - 40, 0);
            return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
          }
          return 'rgba(148, 163, 184, 0.45)';
        } catch {
          return 'rgba(148, 163, 184, 0.45)';
        }
      };

      return (
        <button
          key={key}
          type="button"
          data-testid={`tid.tools.shapeColor.swatch.${isTransparent || !color ? 'transparent' : color.replace('#', '').toLowerCase()}`}
          className="relative flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
          onClick={() => {
            if (options?.trackOnSelect && !isTransparent) {
              trackRecentColor(color);
            }
            handleColorChange(color);
          }}
          aria-label={`Apply color ${color}`}
          aria-pressed={selected}
        >
          <span className="absolute inset-[1px] rounded-full" />
          <span
            className="relative inline-flex h-full w-full items-center justify-center rounded-full border"
            style={{
              backgroundColor: isTransparent ? '#ffffff' : color,
              borderColor: resolveBorderColor(),
              backgroundImage: isTransparent
                ? 'radial-gradient(circle at 30% 30%, rgba(148, 163, 184, 0.05) 0%, transparent 55%)'
                : undefined
            }}
          >
            {isTransparent && (
              <span className="absolute h-[1px] w-full rotate-45 rounded-full bg-slate-400/70" />
            )}
          </span>
          {selected && (
            <Check className="absolute right-auto bottom-auto h-3 w-3 text-white mix-blend-difference drop-shadow" />
          )}
        </button>
      );
    },
    [handleColorChange, isColorSelected, trackRecentColor]
  );

  const renderAddCustomButton = React.useCallback(
    (key: string) => (
      <button
        key={key}
        type="button"
        data-testid="tid.tools.shapeColor.custom"
        onClick={() => {
          setShowCustomPicker(true);
        }}
        className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-slate-400 transition hover:border-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
        aria-label={t('properties.pickCustomColor')}
      >
        <Plus className="h-3 w-3" />
      </button>
    ),
    [setShowCustomPicker, t]
  );

  React.useEffect(() => {
    if (!open) {
      setShowCustomPicker(false);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.editor.shapeToolbar.fill"
          disabled={isDisabled}
          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('properties.fillColor', { defaultValue: 'Fill color' })}
        >
          <div 
            className="w-5 h-5 rounded-full border border-border"
            style={{ backgroundColor: selectedColor }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="relative w-[168px] p-3 overflow-visible" align="end">
        {/* Label Header */}
        <div className="mb-3 pb-2 border-b border-border">
          <h3 className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{t('tools.fillColor')}</h3>
        </div>

        <div className="space-y-3">
          {/* Preset colors grid */}
          <div className="grid grid-cols-4 gap-2">
            {flattenedPalette.map((color, index) => renderColorChip(color, `palette-${color}-${index}`))}
          </div>

          {/* Recently used custom colors */}
          <div className="grid grid-cols-4 gap-2 pt-1">
            {recentCustomColors.length > 0 ? (
              <>
                {recentCustomColors.map((color, index) =>
                  renderColorChip(color, `recent-${color}-${index}`, { trackOnSelect: true })
                )}
                {renderAddCustomButton('recent-add-button')}
              </>
            ) : (
              renderAddCustomButton('recent-add-empty')
            )}
          </div>
        </div>
        {showCustomPicker && (
          <div
            className="absolute"
            style={{ left: 'calc(100% + 6px)', top: 'calc((3rem + 0.75rem) * 5)' }}
          >
            <div className="flex w-[288px] flex-col gap-3 rounded-[10px] border border-border/60 bg-background p-3 shadow-[0_6px_18px_rgba(2,6,23,0.08)]">
              <AdvancedColorPicker
                color={customColor}
                onColorChange={applyCustomColor}
              />
              <input
                type="color"
                value={customColor}
                onChange={handleCustomColorChange}
                ref={colorInputRef}
                className="absolute h-px w-px opacity-0"
                tabIndex={-1}
                aria-hidden="true"
              />
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  data-testid="tid.tools.shapeColor.customHex"
                  value={customColorDraft}
                  onChange={handleCustomColorDraftChange}
                  className="h-10 w-[120px] rounded-lg border border-border/60 px-3 text-sm shadow-inner"
                  placeholder="#000000"
                />
                <button
                  type="button"
                  data-testid="tid.tools.shapeColor.eyedropper"
                  onClick={handleEyeDropperPick}
                  className="flex h-10 w-10 min-w-[40px] items-center justify-center rounded-lg border border-border/60 bg-muted/60 transition hover:bg-muted"
                  aria-label={t('properties.pickFromCanvas', 'Pick color from canvas')}
                  disabled={!canUseEyeDropper || isPickingColor}
                >
                  <Pipette className="h-4 w-4 text-foreground/80" />
                </button>
                <Button
                  size="sm"
                  data-testid="tid.tools.shapeColor.customApply"
                  onClick={handleCustomColorApply}
                  className="h-10 px-4 text-sm font-medium"
                  disabled={!HEX_FULL_REGEX.test(customColorDraft) && !HEX_SHORT_REGEX.test(customColorDraft)}
                >
                  {t('properties.apply', 'Apply')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export class ShapeColorTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'shape-color',
    name: 'tools.fillColor',
    icon: 'palette',
    category: 'style',
    description: 'Change fill color of selected shapes',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.ALL_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context) || context.selection.size === 0) return false;
    return context.selectionSummary?.contextType !== 'image';
  }

  execute(params?: { color: string }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params?.color) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);

    const selectionState = useTableCellStore.getState().selections;
    const tableCellKeysByNodeId: Record<string, TableCellKey[]> = {};
    const tableNodeIds: string[] = [];
    const remainingNodeIds: string[] = [];

    selectedIds.forEach((nodeId) => {
      const keys = selectionState[nodeId]?.selectedKeys ?? [];
      if (keys.length > 0) {
        tableNodeIds.push(nodeId);
        tableCellKeysByNodeId[nodeId] = keys;
        return;
      }
      remainingNodeIds.push(nodeId);
    });

    const commands: ICommand[] = [];

    if (tableNodeIds.length > 0) {
      commands.push(new UpdateTableCellStylesCommand({
        nodeIds: tableNodeIds,
        cellKeysByNodeId: tableCellKeysByNodeId,
        stylePatch: { fillColor: params.color },
      }));
    }

    if (remainingNodeIds.length > 0) {
      console.log('[ShapeColorTool] Creating ChangeNodeStyleCommand for nodes:', selectedIds);
      commands.push(new ChangeNodeStyleCommand(remainingNodeIds, 'fillColor', params.color));
    }

    if (commands.length === 0) return null;
    
    // Track styling applied (aggregated + first-time)
    SessionMetrics.increment('styling_applied');
    SessionMetrics.firstTime('styling_applied');
    return commands.length === 1 ? commands[0] : new CompositeCommand('Change Fill', commands);
  }

  getToolbarComponent() {
    return ShapeColorToolComponent;
  }

  getFloatingPanelComponent() {
    return ShapeColorToolComponent;
  }
}
